'use client';

/** @jsxRuntime classic */
/** @jsx jsx */

import { jsx } from '@emotion/react';
import Button from '@egalite/ui/Buttons';
import ErrorList from '@egalite/ui/ErrorList';
import { InputField } from '@egalite/ui/Form';
import InputFieldGroup from '@egalite/ui/Form/InputFieldGroup';
import SelectField from '@egalite/ui/Form/SelectField';
import { useNotification } from '@egalite/ui/IncluiPCD/NotificationProvider';
import Loading from '@egalite/ui/Loading';
import Typography from '@egalite/ui/Typography';
import dateMask from '@egalite/ui/dateMask';
import Cookies from 'js-cookie';
import { useRouter } from 'next/navigation';
import { FC, useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import authFetch from '../../lib/auth/authFetch';
import useForm from '../../lib/hooks/useForm';
import validationSchema, { initialValues } from '../../lib/validation/candidate/registerData';
import ButtonsPages from './legacy_blocks/ButtonsPages';
import FormPage from './legacy_blocks/CandidateFormPage';
import save from './save';

const Register: FC<{ edit?: boolean; onSuccess?: () => void; onFormValidChange?: (isValid: boolean) => void }> = ({
	edit = false,
	onSuccess = null,
	onFormValidChange
}) => {
	const notification = useNotification();
	const [loadingData, setLoadingData] = useState(true);
	const [candidateData, setCandidateData] = useState(initialValues);
	const [needsCPF, setNeedsCPF] = useState(false);

	const router = useRouter();

	// Verificar se precisa de CPF
	useEffect(() => {
		if (candidateData && !loadingData) {
			const cpfValue = (candidateData as any).cpf || '';
			const hasCPF = cpfValue.replace(/\D/g, '').length === 11;
			setNeedsCPF(!hasCPF);
		}
	}, [candidateData, loadingData]);

	// Schema de validação condicional: se não tem CPF, adicionar validação de CPF
	const conditionalSchema = useMemo(() => {
		if (needsCPF) {
			// Adicionar validação de CPF ao schema existente
			const getDigit = (num: number) => {
				const digit = (num * 10) % 11;
				return 10 > digit ? digit.toString() : '0';
			};

			const checkCPF = (docNumber: string) => {
				if (typeof docNumber !== 'string') {
					return false;
				}
				const cpf = docNumber.replace(/\D/g, '').split('');
				if (cpf.length < 11) {
					return false;
				}
				if (cpf.filter(i => i === cpf[0]).length === cpf.length) {
					return false;
				}
				const vals = cpf
					.map(i => parseInt(i))
					.reduce(
						(acc, i, index) => {
							if (index < 9) {
								acc[0] = acc[0] + i * (10 - index);
							}
							if (index < 10) {
								acc[1] = acc[1] + i * (11 - index);
							}
							return acc;
						},
						[0, 0],
					);

				if (getDigit(vals[0]) === cpf[9] && getDigit(vals[1]) === cpf[10]) {
					return true;
				}
				return false;
			};

			return validationSchema.shape({
				cpf: yup
					.string()
					.matches(/([0-9]{3}.[0-9]{3}.[0-9]{3}-[0-9]{2})/, { message: 'CPF é obrigatório!', excludeEmptyString: true })
					.required('CPF é obrigatório!')
					.test('cpf', 'CPF inválido!', checkCPF),
			});
		}
		return validationSchema;
	}, [needsCPF]);

	const [formController, getFieldProps, { loading, submitting, success, errors, data, setErrors }] = useForm({
		initialValues: candidateData,
		validationSchema: conditionalSchema,
		mutation: data => save('/api/v1/candidate/registerData', data),
		onSuccess:
			onSuccess != null
				? () => onSuccess()
				: () => {
					router.push(`/candidato/form/personalData1`, { scroll: true });
				},
	});

	// Interceptar erros do backend e associar ao campo CPF se for erro de duplicata
	useEffect(() => {
		if (errors && errors.length > 0 && needsCPF) {
			const cpfDuplicateError = errors.find((error: string) => 
				typeof error === 'string' &&
				error.toLowerCase().includes('cpf') && 
				(error.toLowerCase().includes('cadastrado') || error.toLowerCase().includes('já está'))
			);
			
			if (cpfDuplicateError) {
				// Associar erro ao campo CPF
				formController.setFieldError('cpf', cpfDuplicateError);
			}
		}
	}, [errors, needsCPF, formController]);

	// Verificar se o formulário está válido e notificar o componente pai
	useEffect(() => {
		if (onFormValidChange && !loadingData) {
			const checkFormValid = async () => {
				const mainDisabilityType = (formController.values as any).mainDisabilityType || '';
				if (!mainDisabilityType.trim()) {
					onFormValidChange(false);
					return;
				}

				// Se não precisa de CPF, o formulário está válido (tipo de deficiência já preenchido)
				if (!needsCPF) {
					onFormValidChange(true);
					return;
				}

				// Se precisa de CPF, verificar se o CPF está preenchido e válido
				try {
					const cpfValue = (formController.values as any).cpf || '';
					const cpfDigits = cpfValue.replace(/\D/g, '');

					if (cpfDigits.length === 11) {
						await conditionalSchema.validateAt('cpf', { cpf: cpfValue });
						onFormValidChange(true);
					} else {
						onFormValidChange(false);
					}
				} catch (validationError) {
					onFormValidChange(false);
				}
			};

			const timeoutId = setTimeout(checkFormValid, 300);
			return () => clearTimeout(timeoutId);
		}
	}, [formController.values, formController.touched, conditionalSchema, loadingData, onFormValidChange, needsCPF]);
	useEffect(() => {
		if (loadingData) {
			authFetch('/api/v2/candidate/profile', Cookies.get('token'))
				.then(res => res.json())
				.then(data => {
					if (data.success === true) {
						const newValues = Object.keys(initialValues).reduce((acc, key) => {
							acc[key] = data.data[key] ?? initialValues[key];
							return acc;
						}, {});
						// Adicionar CPF se existir no perfil
						if (data.data?.cpf) {
							(newValues as any).cpf = data.data.cpf;
						}
						setCandidateData(newValues as typeof initialValues);
						setLoadingData(false);
					} else {
						notification.error('Erro ao carregar dados do candidato');
						setLoadingData(false);
					}
				})
				.catch(() => {
					notification.error('Erro ao carregar dados do candidato');
					setLoadingData(false);
				});
		}
	}, [formController, router, loadingData, notification]);

	if (loadingData) {
		return <Loading fullScreen={true}>carregando dados</Loading>;
	}

	return (
		<FormPage
			actualStep={1}
			formActualStep={1}
			formTotalSteps={1}
			progress={100}
			formTitle={'Dados cadastrais'}
			// previousForm="/"
			showStepsMenu={false}
			// showBackButton={false}
			modal={edit}
		>
			<form onSubmit={formController.handleSubmit}>
				<div className="mb-16 md:mb-24">
					<h1 className='text-4xl font-bold mb-4'>Complete seu perfil</h1>
					<InputField id="fullname" type="text" label="Nome completo" {...getFieldProps('fullname')} />

					{/* Mostrar CPF apenas se não tiver CPF */}
					{needsCPF && (
						<InputField
							id="cpf"
							type="text"
							label="CPF"
							maskOptions={{ mask: '000.000.000-00', lazy: false, placeholderChar: '_' }}
							{...getFieldProps('cpf')}
						/>
					)}

					<InputField
						id="email"
						type="email"
						label="Email"
						{...getFieldProps('email')}
					/>
					<InputFieldGroup
						id="phone"
						type="text"
						label="Telefone"
						maskOptions={{
							mask: [
								{ mask: '(00) 0000-0000', lazy: false, placeholderChar: '_' },
								{ mask: '(00) 00000-0000', lazy: false, placeholderChar: '_' },
							],
						}}
						{...getFieldProps('phone')}
					/>
					<InputField
						id="birthdate"
						type="text"
						label="Data de nascimento"
						maskOptions={dateMask}
						{...getFieldProps('birthdate')}
					/>
					<SelectField
						id="mainDisabilityType"
						label="Tipo de deficiência"
						placeholder="Selecione..."
						className="my-[3.6rem]"
						options={[
							{ value: 'Deficiência Auditiva', label: 'Deficiência Auditiva' },
							{ value: 'Deficiência Física', label: 'Deficiência Física' },
							{ value: 'Deficiência Intelectual', label: 'Deficiência Intelectual' },
							{ value: 'Deficiência Psicossocial', label: 'Deficiência Psicossocial' },
							{ value: 'Deficiência Visual', label: 'Deficiência Visual' },
							{ value: 'Transtorno do Espectro Autista', label: 'Transtorno do Espectro Autista' },
							{ value: 'Reabilitado do INSS', label: 'Reabilitado do INSS' },
						]}
						{...getFieldProps('mainDisabilityType')}
					/>
					<SelectField
						id="gender"
						label="Gênero"
						className="my-[3.6rem]"
						options={[
							{ value: 'Feminino', label: 'Feminino' },
							{ value: 'Masculino', label: 'Masculino' },
							{ value: 'Prefiro não responder', label: 'Prefiro não responder' },
							{ value: 'Outro', label: 'Personalizar' },
						]}
						{...getFieldProps('gender')}
					/>
					{['Outro'].includes(formController.values.gender) ? (
						<InputField id="genderOther" type="text" label="Descrição do gênero" {...getFieldProps('genderOther')} />
					) : null}

					<div className="mt-5">
						{/* Filtrar erro de CPF duplicado do ErrorList (já é exibido embaixo do campo) */}
						{(() => {
							const filteredErrors = { ...formController.errors };
							// Remover erro de CPF duplicado da lista de erros
							if (filteredErrors.cpf) {
								const cpfError = filteredErrors.cpf;
								if (typeof cpfError === 'string' && 
									(cpfError.toLowerCase().includes('cadastrado') || cpfError.toLowerCase().includes('já está'))) {
									delete filteredErrors.cpf;
								}
							}
							return <ErrorList errors={filteredErrors} touched={formController.touched} />;
						})()}

						{errors != null &&
							errors
								.filter((err: string) => {
									// Filtrar erros de CPF duplicado para não exibir na lista geral
									// (já são exibidos embaixo do campo CPF)
									if (typeof err === 'string') {
										const isCpfDuplicate = 
											err.toLowerCase().includes('cpf') && 
											(err.toLowerCase().includes('cadastrado') || err.toLowerCase().includes('já está'));
										return !isCpfDuplicate;
									}
									return true;
								})
								.map((err, index) => (
									<Typography key={`err-${index}`} variant="body2" color="error.dark">
										{err}
									</Typography>
								))}
					</div>
				</div>
				<div className="md:mt-24 mt-16">
					{edit ? (
						<Button type="submit" variant="primary" className="w-full">
							Salvar
						</Button>
					) : (
						<ButtonsPages hideBack={true} />
					)}
				</div>
			</form>
		</FormPage>
	);
};

export default Register;
